import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  getAuth,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail
} from '@firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  collection,
  query,
  where
} from 'firebase/firestore';
import { auth, db } from './config';

// Auth services
export const signUp = async (email, password, userData) => {
  try {
    console.log('Starting signup process for new user...');
    
    // First check if email exists in Firebase Auth
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      if (methods && methods.length > 0) {
        throw new Error('This email is already registered. Please use a different email address.');
      }
    } catch (error) {
      if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      }
      throw error;
    }

    // Then check if email exists in Firestore
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      throw new Error('This email already exists in our database. Please use a different email address.');
    }

    // If we get here, the email is unique, so create the user
    let newUser;
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      newUser = userCredential.user;
      console.log('New user account created:', newUser.uid);
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('This email was just registered. Please try a different email address.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Please choose a stronger password. It should be at least 6 characters long.');
      } else if (error.code === 'auth/operation-not-allowed') {
        throw new Error('Account creation is currently disabled. Please contact support.');
      }
      throw error;
    }

    // Create the user document in Firestore
    const userRef = doc(db, 'users', newUser.uid);
    const userDoc = {
      email: newUser.email.toLowerCase(),
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      role: (userData?.role || 'user').toLowerCase(),
      status: (userData?.status || 'active').toLowerCase(),
      assignedLocations: userData?.assignedLocations || [],
      locationId: userData?.assignedLocations?.[0]?.id || null,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    console.log('Creating user document:', userDoc);
    await setDoc(userRef, userDoc);
    console.log('User document created successfully');

    // Sign out the new user to prevent session change
    await auth.signOut();

    return { user: newUser, userDoc };
  } catch (error) {
    console.error('Error in signUp:', error);
    throw error;
  }
};

export const signIn = async (email, password) => {
  console.time('signIn'); // Debug timing
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    console.timeEnd('signIn'); // Debug timing
    return result;
  } catch (error) {
    console.timeEnd('signIn'); // Debug timing even on error
    console.error('Sign in error:', error);
    
    // Convert Firebase errors to user-friendly messages
    if (error.code === 'auth/user-not-found') {
      throw new Error('User not found. Please check your email and password.');
    } else if (error.code === 'auth/wrong-password') {
      throw new Error('Incorrect password. Please try again.');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Please enter a valid email address.');
    }
    
    throw error;
  }
};

export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error in logOut:', error);
    throw error;
  }
};

// Admin creation
export const createAdminUser = async () => {
  try {
    const adminEmail = 'admin@example.com';
    const adminPassword = 'admin123';

    // Check if admin already exists
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const adminExists = snapshot.docs.some(doc => doc.data().email === adminEmail);

    if (adminExists) {
      throw new Error('Admin user already exists');
    }

    // Create auth user
    console.log('Creating admin auth account...');
    const userCredential = await createUserWithEmailAndPassword(auth, adminEmail, adminPassword);
    const user = userCredential.user;

    // Create admin document
    console.log('Creating admin Firestore document...');
    await setDoc(doc(db, 'users', user.uid), {
      email: adminEmail,
      firstName: 'Admin',
      lastName: 'User',
      role: 'admin',
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    console.log('Admin user created successfully');
    return userCredential;
  } catch (error) {
    console.error('Error creating admin:', error);
    throw error;
  }
};

// Password management
export const updateUserPassword = async (userId) => {
  try {
    console.log('Starting password reset for user:', userId);
    
    // Get the user's auth record
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    // Get the user's email from their document
    const userEmail = userDoc.data().email;
    if (!userEmail) {
      throw new Error('User email not found');
    }

    // Send password reset email
    await sendPasswordResetEmail(auth, userEmail);
    
    // Update the user document
    await updateDoc(userRef, {
      updatedAt: new Date().toISOString(),
      passwordResetRequested: new Date().toISOString()
    });

    console.log('Password reset email sent successfully');
    return { 
      success: true,
      message: 'A password reset link has been sent to the user\'s email address.' 
    };
  } catch (error) {
    console.error('Error in password reset:', error);
    throw new Error(`Failed to send password reset email: ${error.message}`);
  }
};

// Firestore services
export const createUserDocument = async (uid, userData) => {
  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
      ...userData,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error in createUserDocument:', error);
    throw error;
  }
};

export const getAllUsers = async () => {
  try {
    console.log('Fetching all users...');
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    console.log('Number of users found:', snapshot.size);
    
    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('User data:', users);
    return users;
  } catch (error) {
    console.error('Error in getAllUsers:', error);
    throw error;
  }
};

export const getUserDocument = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const docSnap = await getDoc(userRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    console.error('Error in getUserDocument:', error);
    throw error;
  }
};

export const updateUserDocument = async (uid, data) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error in updateUserDocument:', error);
    throw error;
  }
};